/**
 * Custom TipTap table extensions — replaces @tiptap/extension-table and prosemirror-tables.
 *
 * Exports: CustomTable, CustomTableRow, CustomTableHeader, CustomTableCell
 *
 * Features:
 * - Insert / delete table
 * - Add / delete rows and columns
 * - Column resize (capture-phase DOM listeners)
 * - Row resize (capture-phase DOM listeners)
 * - Tab / Shift-Tab navigation between cells
 * - Row hiding (data-row-hidden) and row height persistence
 */

import { Node, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import type { Node as PmNode, ResolvedPos } from '@tiptap/pm/model';

/* ------------------------------------------------------------------ */
/*  Type augmentation so editor.chain().insertTable() etc. are typed   */
/* ------------------------------------------------------------------ */

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    table: {
      insertTable: (opts?: {
        rows?: number;
        cols?: number;
        withHeaderRow?: boolean;
      }) => ReturnType;
      deleteTable: () => ReturnType;
      addColumnBefore: () => ReturnType;
      addColumnAfter: () => ReturnType;
      deleteColumn: () => ReturnType;
      addRowBefore: () => ReturnType;
      addRowAfter: () => ReturnType;
      deleteRow: () => ReturnType;
      goToNextCell: () => ReturnType;
      goToPreviousCell: () => ReturnType;
    };
  }
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function findTableDepth($pos: ResolvedPos): number {
  for (let d = $pos.depth; d > 0; d--) {
    if ($pos.node(d).type.name === 'table') return d;
  }
  return -1;
}

function cellPositionAt(
  table: PmNode,
  tableStart: number,
  rowIdx: number,
  colIdx: number,
): number {
  let off = tableStart;
  for (let r = 0; r < rowIdx; r++) off += table.child(r).nodeSize;
  off += 1; // row open tag
  const row = table.child(rowIdx);
  for (let c = 0; c < colIdx; c++) off += row.child(c).nodeSize;
  return off;
}

/* ------------------------------------------------------------------ */
/*  Column resize plugin (capture-phase)                               */
/* ------------------------------------------------------------------ */

const COL_RESIZE_ZONE = 5;
const COL_RESIZE_CLASS = 'col-resize-active';
const CELL_MIN_WIDTH = 48;

function columnResizePlugin() {
  return new Plugin({
    key: new PluginKey('columnResize'),
    view(editorView) {
      let isDragging = false;
      let dragColIndex = -1;
      let startX = 0;
      let startWidth = 0;
      let lastComputedWidth = 0;
      let styleEl: HTMLStyleElement | null = null;
      let resizeIndicator: HTMLDivElement | null = null;
      let dragTable: HTMLTableElement | null = null;
      let cellLeft = 0;

      function findResizeCol(e: MouseEvent) {
        const target = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
        if (!target) return null;
        const cell = target.closest?.('td, th');
        if (!cell || !editorView.dom.contains(cell)) return null;
        const tr = cell.closest('tr');
        if (!tr) return null;
        const table = tr.closest('table');
        if (!table) return null;
        const rect = cell.getBoundingClientRect();
        if (Math.abs(rect.right - e.clientX) > COL_RESIZE_ZONE) return null;
        const colIndex = Array.from(tr.children).indexOf(cell);
        return { colIndex, width: rect.width, table, cellLeft: rect.left };
      }

      function onMouseMoveEditor(e: MouseEvent) {
        if (isDragging) return;
        editorView.dom.classList.toggle(COL_RESIZE_CLASS, !!findResizeCol(e));
      }

      function onMouseLeaveEditor() {
        if (!isDragging) editorView.dom.classList.remove(COL_RESIZE_CLASS);
      }

      function updateColStyle(w: number) {
        if (!styleEl) return;
        const nth = dragColIndex + 1;
        styleEl.textContent =
          `[data-col-resizing="${nth}"] table td:nth-child(${nth}),` +
          `[data-col-resizing="${nth}"] table th:nth-child(${nth})` +
          `{width:${w}px !important;min-width:${w}px !important;max-width:${w}px !important}`;
      }

      function onMouseDownCapture(e: MouseEvent) {
        if (isDragging || e.button !== 0 || !editorView.editable) return;
        const hit = findResizeCol(e);
        if (!hit) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        isDragging = true;
        dragColIndex = hit.colIndex;
        startX = e.clientX;
        startWidth = hit.width;
        lastComputedWidth = hit.width;

        editorView.dom.setAttribute('data-col-resizing', String(hit.colIndex + 1));
        styleEl = document.createElement('style');
        document.head.appendChild(styleEl);
        updateColStyle(hit.width);

        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        editorView.dom.classList.add(COL_RESIZE_CLASS);

        dragTable = hit.table;
        cellLeft = hit.cellLeft;

        document.addEventListener('mousemove', onDragMove);
        document.addEventListener('mouseup', onDragEnd);
      }

      function onDragMove(e: MouseEvent) {
        lastComputedWidth = Math.max(CELL_MIN_WIDTH, startWidth + (e.clientX - startX));
        updateColStyle(lastComputedWidth);
        const indicatorLeft = cellLeft + lastComputedWidth - 1;
        if (!resizeIndicator && dragTable) {
          const tRect = dragTable.getBoundingClientRect();
          resizeIndicator = document.createElement('div');
          resizeIndicator.style.cssText = `position:fixed;left:${indicatorLeft}px;top:${tRect.top}px;width:4px;height:${tRect.height}px;background:#7047eb;pointer-events:none;z-index:9999;`;
          document.body.appendChild(resizeIndicator);
        } else if (resizeIndicator) {
          resizeIndicator.style.left = `${indicatorLeft}px`;
        }
      }

      function cleanupDrag() {
        if (styleEl) { styleEl.remove(); styleEl = null; }
        if (resizeIndicator) { resizeIndicator.remove(); resizeIndicator = null; }
        dragTable = null;
        editorView.dom.removeAttribute('data-col-resizing');
      }

      function onDragEnd() {
        document.removeEventListener('mousemove', onDragMove);
        document.removeEventListener('mouseup', onDragEnd);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        editorView.dom.classList.remove(COL_RESIZE_CLASS);
        cleanupDrag();

        const colIdx = dragColIndex;
        const finalWidth = lastComputedWidth;
        isDragging = false;

        if (Math.abs(finalWidth - startWidth) < 2) return;

        const table = editorView.dom.querySelector('table');
        if (!table) return;

        const rounded = Math.round(finalWidth);
        try {
          const rows = table.querySelectorAll('tr');
          const pmTr = editorView.state.tr;
          rows.forEach((row) => {
            const cell = row.children[colIdx] as HTMLElement | undefined;
            if (!cell) return;
            const pos = editorView.posAtDOM(cell, 0);
            const $pos = editorView.state.doc.resolve(pos);
            for (let d = $pos.depth; d > 0; d--) {
              const n = $pos.node(d);
              if (n.type.name === 'tableCell' || n.type.name === 'tableHeader') {
                pmTr.setNodeMarkup($pos.before(d), undefined, {
                  ...n.attrs,
                  colwidth: [rounded],
                });
                break;
              }
            }
          });
          if (pmTr.docChanged) editorView.dispatch(pmTr);
        } catch (_) { /* ignore */ }
      }

      editorView.dom.addEventListener('mousemove', onMouseMoveEditor);
      editorView.dom.addEventListener('mouseleave', onMouseLeaveEditor);
      editorView.dom.addEventListener('mousedown', onMouseDownCapture, true);

      return {
        destroy() {
          editorView.dom.removeEventListener('mousemove', onMouseMoveEditor);
          editorView.dom.removeEventListener('mouseleave', onMouseLeaveEditor);
          editorView.dom.removeEventListener('mousedown', onMouseDownCapture, true);
          editorView.dom.classList.remove(COL_RESIZE_CLASS);
          cleanupDrag();
          if (isDragging) {
            document.removeEventListener('mousemove', onDragMove);
            document.removeEventListener('mouseup', onDragEnd);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
          }
        },
      };
    },
  });
}

/* ------------------------------------------------------------------ */
/*  Row resize plugin (capture-phase)                                  */
/* ------------------------------------------------------------------ */

const ROW_RESIZE_ZONE = 6;
const ROW_RESIZE_CLASS = 'row-resize-active';

function rowResizePlugin() {
  return new Plugin({
    key: new PluginKey('rowResize'),
    view(editorView) {
      let isDragging = false;
      let dragRowIndex = -1;
      let startY = 0;
      let startHeight = 0;
      let lastComputedHeight = 0;
      let styleEl: HTMLStyleElement | null = null;
      let resizeIndicator: HTMLDivElement | null = null;
      let dragTable: HTMLTableElement | null = null;
      let rowTop = 0;

      function findResizeRow(e: MouseEvent) {
        const target = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
        if (!target) return null;
        const cell = target.closest?.('td, th');
        if (!cell || !editorView.dom.contains(cell)) return null;
        const tr = cell.closest('tr');
        if (!tr) return null;
        const table = tr.closest('table');
        if (!table) return null;
        const rect = tr.getBoundingClientRect();
        if (Math.abs(rect.bottom - e.clientY) > ROW_RESIZE_ZONE) return null;
        const rows = table.querySelectorAll('tr');
        const index = Array.from(rows).indexOf(tr);
        return { tr, index, table };
      }

      function onMouseMoveEditor(e: MouseEvent) {
        if (isDragging) return;
        editorView.dom.classList.toggle(ROW_RESIZE_CLASS, !!findResizeRow(e));
      }

      function onMouseLeaveEditor() {
        if (!isDragging) editorView.dom.classList.remove(ROW_RESIZE_CLASS);
      }

      function updateRowStyle(h: number) {
        if (!styleEl) return;
        const nth = dragRowIndex + 1;
        styleEl.textContent =
          `[data-row-resizing="${nth}"] table tr:nth-child(${nth}){height:${h}px !important}` +
          `[data-row-resizing="${nth}"] table tr:nth-child(${nth}) td,` +
          `[data-row-resizing="${nth}"] table tr:nth-child(${nth}) th` +
          `{height:${h}px !important;min-height:${h}px !important}`;
      }

      function onMouseDownCapture(e: MouseEvent) {
        if (isDragging || e.button !== 0 || !editorView.editable) return;
        const hit = findResizeRow(e);
        if (!hit) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        isDragging = true;
        dragRowIndex = hit.index;
        startY = e.clientY;
        startHeight = hit.tr.getBoundingClientRect().height;
        lastComputedHeight = startHeight;

        editorView.dom.setAttribute('data-row-resizing', String(hit.index + 1));
        styleEl = document.createElement('style');
        document.head.appendChild(styleEl);
        updateRowStyle(startHeight);

        document.body.style.cursor = 'row-resize';
        document.body.style.userSelect = 'none';
        editorView.dom.classList.add(ROW_RESIZE_CLASS);

        dragTable = hit.table;
        rowTop = hit.tr.getBoundingClientRect().top;

        document.addEventListener('mousemove', onDragMove);
        document.addEventListener('mouseup', onDragEnd);
      }

      function onDragMove(e: MouseEvent) {
        lastComputedHeight = Math.max(28, startHeight + (e.clientY - startY));
        updateRowStyle(lastComputedHeight);
        const indicatorTop = rowTop + lastComputedHeight - 1;
        if (!resizeIndicator && dragTable) {
          const tRect = dragTable.getBoundingClientRect();
          resizeIndicator = document.createElement('div');
          resizeIndicator.style.cssText = `position:fixed;left:${tRect.left}px;top:${indicatorTop}px;width:${tRect.width}px;height:4px;background:#7047eb;pointer-events:none;z-index:9999;`;
          document.body.appendChild(resizeIndicator);
        } else if (resizeIndicator) {
          resizeIndicator.style.top = `${indicatorTop}px`;
        }
      }

      function cleanupDrag() {
        if (styleEl) { styleEl.remove(); styleEl = null; }
        if (resizeIndicator) { resizeIndicator.remove(); resizeIndicator = null; }
        dragTable = null;
        editorView.dom.removeAttribute('data-row-resizing');
      }

      function onDragEnd() {
        document.removeEventListener('mousemove', onDragMove);
        document.removeEventListener('mouseup', onDragEnd);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        editorView.dom.classList.remove(ROW_RESIZE_CLASS);
        cleanupDrag();

        const finalHeight = lastComputedHeight;
        const rowIdx = dragRowIndex;
        isDragging = false;

        if (Math.abs(finalHeight - startHeight) < 2) return;

        const table = editorView.dom.querySelector('table');
        if (!table) return;
        const targetRow = table.querySelectorAll('tr')[rowIdx];
        if (!targetRow) return;
        const firstCell = targetRow.querySelector('td, th');
        if (!firstCell) return;

        try {
          const pos = editorView.posAtDOM(firstCell, 0);
          const $pos = editorView.state.doc.resolve(pos);
          for (let d = $pos.depth; d > 0; d--) {
            if ($pos.node(d).type.name === 'tableRow') {
              const trPos = $pos.before(d);
              const trNode = editorView.state.doc.nodeAt(trPos);
              if (trNode) {
                const pmTr = editorView.state.tr;
                pmTr.setNodeMarkup(trPos, undefined, {
                  ...trNode.attrs,
                  height: `${Math.round(finalHeight)}px`,
                });
                editorView.dispatch(pmTr);
              }
              break;
            }
          }
        } catch (_) { /* ignore */ }
      }

      editorView.dom.addEventListener('mousemove', onMouseMoveEditor);
      editorView.dom.addEventListener('mouseleave', onMouseLeaveEditor);
      editorView.dom.addEventListener('mousedown', onMouseDownCapture, true);

      return {
        destroy() {
          editorView.dom.removeEventListener('mousemove', onMouseMoveEditor);
          editorView.dom.removeEventListener('mouseleave', onMouseLeaveEditor);
          editorView.dom.removeEventListener('mousedown', onMouseDownCapture, true);
          editorView.dom.classList.remove(ROW_RESIZE_CLASS);
          cleanupDrag();
          if (isDragging) {
            document.removeEventListener('mousemove', onDragMove);
            document.removeEventListener('mouseup', onDragEnd);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
          }
        },
      };
    },
  });
}

/* ------------------------------------------------------------------ */
/*  Cell attributes (shared between tableCell and tableHeader)         */
/* ------------------------------------------------------------------ */

function cellAttributes() {
  return {
    colspan: { default: 1 },
    rowspan: { default: 1 },
    colwidth: {
      default: null,
      parseHTML: (el: HTMLElement) => {
        const attr = el.getAttribute('colwidth');
        if (attr) return attr.split(',').map((w) => parseInt(w, 10));
        const w = el.style.width;
        if (w && w.endsWith('px')) return [parseInt(w, 10)];
        return null;
      },
      renderHTML: (attrs: Record<string, unknown>) => {
        const cw = attrs.colwidth as number[] | null;
        if (!cw) return {};
        return { colwidth: cw.join(','), style: `width: ${cw[0]}px` };
      },
    },
  };
}

/* ------------------------------------------------------------------ */
/*  CustomTableCell                                                    */
/* ------------------------------------------------------------------ */

export const CustomTableCell = Node.create({
  name: 'tableCell',
  content: 'block+',
  isolating: true,
  parseHTML() {
    return [{ tag: 'td' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['td', mergeAttributes(HTMLAttributes), 0];
  },
  addAttributes() {
    return cellAttributes();
  },
});

/* ------------------------------------------------------------------ */
/*  CustomTableHeader                                                  */
/* ------------------------------------------------------------------ */

export const CustomTableHeader = Node.create({
  name: 'tableHeader',
  content: 'block+',
  isolating: true,
  parseHTML() {
    return [{ tag: 'th' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['th', mergeAttributes(HTMLAttributes), 0];
  },
  addAttributes() {
    return cellAttributes();
  },
});

/* ------------------------------------------------------------------ */
/*  CustomTableRow                                                     */
/* ------------------------------------------------------------------ */

export const CustomTableRow = Node.create({
  name: 'tableRow',
  content: '(tableCell | tableHeader)*',
  parseHTML() {
    return [{ tag: 'tr' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['tr', mergeAttributes(HTMLAttributes), 0];
  },
  addAttributes() {
    return {
      hidden: {
        default: false,
        parseHTML: (el: HTMLElement) => el.hasAttribute('data-row-hidden'),
        renderHTML: (attrs: Record<string, unknown>) =>
          attrs.hidden ? { 'data-row-hidden': '' } : {},
      },
      height: {
        default: null,
        parseHTML: (el: HTMLElement) => el.style.height || null,
        renderHTML: (attrs: Record<string, unknown>) =>
          attrs.height ? { style: `height: ${String(attrs.height)}` } : {},
      },
    };
  },
});

/* ------------------------------------------------------------------ */
/*  CustomTable                                                        */
/* ------------------------------------------------------------------ */

export const CustomTable = Node.create({
  name: 'table',
  group: 'block',
  content: 'tableRow+',
  isolating: true,

  parseHTML() {
    return [{ tag: 'table' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      { class: 'tableWrapper' },
      ['table', mergeAttributes(HTMLAttributes), ['tbody', 0]],
    ];
  },

  addCommands() {
    return {
      /* ----- insertTable ----- */
      insertTable:
        ({ rows = 3, cols = 3, withHeaderRow = true } = {}) =>
        ({ tr, dispatch, editor }) => {
          const { schema } = editor;
          const thType = schema.nodes.tableHeader!;
          const tdType = schema.nodes.tableCell!;
          const trType = schema.nodes.tableRow!;
          const tType = schema.nodes.table!;
          const mkCells = (header: boolean) =>
            Array.from({ length: cols }, () =>
              (header ? thType : tdType).createAndFill()!,
            );
          const tableRows = Array.from({ length: rows }, (_, i) =>
            trType.create(null, mkCells(withHeaderRow && i === 0)),
          );
          const table = tType.create(null, tableRows);
          if (dispatch) {
            const offset = tr.selection.from + 1;
            tr.replaceSelectionWith(table)
              .scrollIntoView()
              .setSelection(TextSelection.near(tr.doc.resolve(offset)));
          }
          return true;
        },

      /* ----- deleteTable ----- */
      deleteTable:
        () =>
        ({ state, dispatch }) => {
          const d = findTableDepth(state.selection.$from);
          if (d === -1) return false;
          if (dispatch) {
            const pos = state.selection.$from.before(d);
            const node = state.selection.$from.node(d);
            dispatch(state.tr.delete(pos, pos + node.nodeSize));
          }
          return true;
        },

      /* ----- addRowBefore ----- */
      addRowBefore:
        () =>
        ({ state, dispatch }) => {
          const $pos = state.selection.$from;
          const td = findTableDepth($pos);
          if (td === -1) return false;
          const rd = td + 1;
          if ($pos.depth < rd) return false;
          const row = $pos.node(rd);
          const tdType = state.schema.nodes.tableCell!;
          const trType = state.schema.nodes.tableRow!;
          const newCells = Array.from({ length: row.childCount }, () =>
            tdType.createAndFill()!,
          );
          const newRow = trType.create(null, newCells);
          if (dispatch) {
            dispatch(state.tr.insert($pos.before(rd), newRow));
          }
          return true;
        },

      /* ----- addRowAfter ----- */
      addRowAfter:
        () =>
        ({ state, dispatch }) => {
          const $pos = state.selection.$from;
          const td = findTableDepth($pos);
          if (td === -1) return false;
          const rd = td + 1;
          if ($pos.depth < rd) return false;
          const row = $pos.node(rd);
          const tdType = state.schema.nodes.tableCell!;
          const trType = state.schema.nodes.tableRow!;
          const newCells = Array.from({ length: row.childCount }, () =>
            tdType.createAndFill()!,
          );
          const newRow = trType.create(null, newCells);
          if (dispatch) {
            dispatch(state.tr.insert($pos.after(rd), newRow));
          }
          return true;
        },

      /* ----- deleteRow ----- */
      deleteRow:
        () =>
        ({ state, dispatch }) => {
          const $pos = state.selection.$from;
          const td = findTableDepth($pos);
          if (td === -1) return false;
          const table = $pos.node(td);
          if (table.childCount <= 1) return false;
          const rd = td + 1;
          if ($pos.depth < rd) return false;
          if (dispatch) {
            const rowPos = $pos.before(rd);
            const row = $pos.node(rd);
            dispatch(state.tr.delete(rowPos, rowPos + row.nodeSize));
          }
          return true;
        },

      /* ----- addColumnBefore ----- */
      addColumnBefore:
        () =>
        ({ state, dispatch }) => {
          const $pos = state.selection.$from;
          const td = findTableDepth($pos);
          if (td === -1) return false;
          const rd = td + 1;
          const cd = td + 2;
          if ($pos.depth < cd) return false;
          const colIndex = $pos.index(rd);
          const table = $pos.node(td);
          const tableStart = $pos.start(td);

          if (dispatch) {
            const tr = state.tr;
            let off = tableStart;
            const positions: number[] = [];
            for (let r = 0; r < table.childCount; r++) {
              const rowOff = off + 1;
              let cellOff = rowOff;
              for (let c = 0; c < colIndex; c++) cellOff += table.child(r).child(c).nodeSize;
              positions.push(cellOff);
              off += table.child(r).nodeSize;
            }
            const thType = state.schema.nodes.tableHeader!;
            const tdType = state.schema.nodes.tableCell!;
            for (let i = positions.length - 1; i >= 0; i--) {
              const row = table.child(i);
              const isHeader = row.child(0)?.type.name === 'tableHeader';
              tr.insert(positions[i]!, (isHeader ? thType : tdType).createAndFill()!);
            }
            dispatch(tr);
          }
          return true;
        },

      /* ----- addColumnAfter ----- */
      addColumnAfter:
        () =>
        ({ state, dispatch }) => {
          const $pos = state.selection.$from;
          const td = findTableDepth($pos);
          if (td === -1) return false;
          const rd = td + 1;
          const cd = td + 2;
          if ($pos.depth < cd) return false;
          const colIndex = $pos.index(rd);
          const table = $pos.node(td);
          const tableStart = $pos.start(td);

          if (dispatch) {
            const tr = state.tr;
            let off = tableStart;
            const positions: number[] = [];
            for (let r = 0; r < table.childCount; r++) {
              const rowOff = off + 1;
              let cellOff = rowOff;
              for (let c = 0; c <= colIndex; c++) cellOff += table.child(r).child(c).nodeSize;
              positions.push(cellOff);
              off += table.child(r).nodeSize;
            }
            const thType = state.schema.nodes.tableHeader!;
            const tdType = state.schema.nodes.tableCell!;
            for (let i = positions.length - 1; i >= 0; i--) {
              const row = table.child(i);
              const isHeader = row.child(0)?.type.name === 'tableHeader';
              tr.insert(positions[i]!, (isHeader ? thType : tdType).createAndFill()!);
            }
            dispatch(tr);
          }
          return true;
        },

      /* ----- deleteColumn ----- */
      deleteColumn:
        () =>
        ({ state, dispatch }) => {
          const $pos = state.selection.$from;
          const td = findTableDepth($pos);
          if (td === -1) return false;
          const rd = td + 1;
          const cd = td + 2;
          if ($pos.depth < cd) return false;
          const table = $pos.node(td);
          if (table.childCount === 0) return false;
          const colIndex = $pos.index(rd);
          if (table.child(0).childCount <= 1) return false;
          const tableStart = $pos.start(td);

          if (dispatch) {
            const tr = state.tr;
            let off = tableStart;
            const positions: number[] = [];
            for (let r = 0; r < table.childCount; r++) {
              const rowOff = off + 1;
              let cellOff = rowOff;
              for (let c = 0; c < colIndex; c++) cellOff += table.child(r).child(c).nodeSize;
              positions.push(cellOff);
              off += table.child(r).nodeSize;
            }
            for (let i = positions.length - 1; i >= 0; i--) {
              const cell = table.child(i).child(colIndex);
              tr.delete(positions[i]!, positions[i]! + cell.nodeSize);
            }
            dispatch(tr);
          }
          return true;
        },

      /* ----- goToNextCell ----- */
      goToNextCell:
        () =>
        ({ state, dispatch }) => {
          const $pos = state.selection.$from;
          const td = findTableDepth($pos);
          if (td === -1) return false;
          const rd = td + 1;
          if ($pos.depth < rd + 1) return false;

          const table = $pos.node(td);
          const rowIdx = $pos.index(td);
          const colIdx = $pos.index(rd);
          const row = table.child(rowIdx);

          let nextRow = rowIdx;
          let nextCol = colIdx + 1;

          if (nextCol >= row.childCount) {
            nextCol = 0;
            nextRow++;
            if (nextRow >= table.childCount) return false;
          }

          const tableStart = $pos.start(td);
          const target = cellPositionAt(table, tableStart, nextRow, nextCol) + 1;
          if (dispatch) {
            dispatch(
              state.tr.setSelection(TextSelection.near(state.doc.resolve(target))),
            );
          }
          return true;
        },

      /* ----- goToPreviousCell ----- */
      goToPreviousCell:
        () =>
        ({ state, dispatch }) => {
          const $pos = state.selection.$from;
          const td = findTableDepth($pos);
          if (td === -1) return false;
          const rd = td + 1;
          if ($pos.depth < rd + 1) return false;

          const table = $pos.node(td);
          const rowIdx = $pos.index(td);
          const colIdx = $pos.index(rd);

          let prevRow = rowIdx;
          let prevCol = colIdx - 1;

          if (prevCol < 0) {
            prevRow--;
            if (prevRow < 0) return false;
            prevCol = table.child(prevRow).childCount - 1;
          }

          const tableStart = $pos.start(td);
          const target = cellPositionAt(table, tableStart, prevRow, prevCol) + 1;
          if (dispatch) {
            dispatch(
              state.tr.setSelection(TextSelection.near(state.doc.resolve(target))),
            );
          }
          return true;
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      Tab: () => {
        if (this.editor.commands.goToNextCell()) return true;
        if (!this.editor.can().addRowAfter()) return false;
        return this.editor.chain().addRowAfter().goToNextCell().run();
      },
      'Shift-Tab': () => this.editor.commands.goToPreviousCell(),
    };
  },

  addProseMirrorPlugins() {
    return [columnResizePlugin(), rowResizePlugin()];
  },
});
